import React, { useRef, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { stockBulkUploadAPI } from "../../config";
import Layout from "../../layout/Layout";

import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import { AlertMessage } from "../../components/AlertMessage";

import moment from "moment";
import { read, utils } from "xlsx";
import { fitColumns } from "../../utils/excelFit";
import { FileDownload } from "@mui/icons-material";
import * as XLSX from "xlsx";

import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Breadcrumbs,
  Link,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { apiData } from "../../services/api_service";
import * as Yup from "yup";
import { Backdrop } from "@mui/material";
import { formatAED, formatNumber } from "../../utils/format";
import { writeXlsxFrozen } from "../../utils/xlsxFreeze";

const StockBulkUpload = (props) => {
  const user = JSON.parse(localStorage.getItem("user"));

  const alertRef = useRef();
  const fileInputRef = useRef();

  const navigate = useNavigate();
  const [showStatus, setShowStatus] = useState(false);

  const [successCount, setSuccessCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [productList, setProductList] = useState([]);
  const [data, setData] = useState([]);
  const [fileLoading, setFileLoading] = useState(false);

  const getValidationSchema = (adjustmentType) => {
    let baseSchema = {
      product_id: Yup.string()
        .typeError("Product is required")
        .required(
          adjustmentType === "transfer"
            ? "For transfer stock transactions, Product is required"
            : "For add/deduct stock adjustments, Product is required"
        ),

      sku: Yup.string()
        .typeError("Product SKU Code is required")
        .required(
          adjustmentType === "transfer"
            ? "For transfer stock transactions, Product SKU is required"
            : "For add/deduct stock adjustments, Product SKU is required"
        ),

      source_store_id: Yup.string()
        .typeError("Source Store is required")
        .required(
          adjustmentType === "transfer"
            ? "For transfer stock transactions, Source Store is required"
            : "For add/deduct stock adjustments, Store is required"
        ),

      adjustmentType: Yup.string()
        .required("Adjustment Type is required (add / deduct / transfer)")
        .test(
          "valid-type",
          "No such adjustment type. Must be 'add', 'deduct', or 'transfer'.",
          function (value) {
            if (!value) return false;
            const validTypes = ["add", "deduct", "transfer"];
            const lower = value.toLowerCase().trim();
            return validTypes.includes(lower);
          }
        )
        .transform((value) => (value ? value.toLowerCase().trim() : value)),

      stockAdjustment: Yup.number()
        .typeError("Quantity must be a number")
        .required("Quantity is required")
        .integer("Quantity must be a whole number not a decimal number")
        .min(1, "Quantity must be greater than 0"),
    };

    if (adjustmentType === "transfer") {
      baseSchema = {
        ...baseSchema,
        destination_store_id: Yup.string()
          .typeError("Destination Store is required")
          .required(
            "For transfer stock transactions, Destination Store is required"
          )
          .test(
            "different-store",
            "Source Store and Destination Store should not be the same",
            function (value) {
              return value !== this.parent.source_store_id;
            }
          ),

        reason: Yup.string().required(
          "For transfer stock transactions, Reason is required"
        ),
      };
    } else if (adjustmentType === "add") {
      baseSchema = {
        ...baseSchema,

        buying_price: Yup.number()
          .typeError("Buying Price must be a number")
          .required("Buying Price is required")
          .moreThan(0, "Buying Price must be greater than 0"), // ⬅️ No zero allowed

        selling_price: Yup.number()
          .typeError("Selling Price must be a number")
          .required("Selling Price is required")
          .moreThan(0, "Selling Price must be greater than 0") // ⬅️ No zero
          .test(
            "is-greater",
            "Selling Price must be greater than Buying Price",
            function (value) {
              const { buying_price } = this.parent;
              if (!buying_price || !value) return false;
              return Number(value) > Number(buying_price); // strictly >
            }
          ),
      };
    } else {
      baseSchema = {
        ...baseSchema,
        manufacture_date: Yup.string()
          .required("Manufacture Date is required")
          .matches(
            /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/,
            "Manufacture Date must be in YYYY-MM-DD format"
          ),

        life_days: Yup.number()
          .typeError("Expiry Days must be a number")
          .required("Expiry Days (Life of Days) is required")
          .min(0, "Expiry Days cannot be negative"),

        note: Yup.string()
          .max(500, "Reference note can be a maximum of 500 characters")
          .nullable(),

        // out_of_stock: Yup.string()
        //   .oneOf(["Yes", "No"], "Out of Stock must be 'Yes' or 'No'")
        //   .default("No")
        //   .notRequired(),
      };
    }

    return Yup.object(baseSchema);
  };

  const [showValidationColumn, setShowValidationColumn] = useState(false);

  const handleExportData = () => {
    if (!productList.length) return;

    const notes = [
      ["Note: Use 'Destination Store' only for stock transfer."],
      [
        "For 'Add': 'Manufacture Date', 'Expiry Days', 'Buying Price', and 'Selling Price' are required.",
      ],
      ["For 'Deduct': 'Manufacture Date' and 'Expiry Days' are required."],
      [
        "For 'Transfer': 'Manufacture Date', 'Expiry Days', 'Buying Price', and 'Selling Price' are not required.",
      ],
      [
        "Additional Info: Ensure date format is DD-MM-YYYY.",
      ],
      [], // Empty row for spacing before headers
    ];

    const headers = [
      "Product",
      "SKU",
      "Source Store",
      "Destination Store",
      "Manufacture Date(DD-MM-YYYY)",
      "Expiry Days",
      "Buying Price",
      "Selling Price",
      "Adjustment Type (add/deduct/transfer)",
      "Quantity",
      "Reason",
      "Reference note(Optional)",
      // "Out of Stock (Yes/No)",
    ];

    // Map the data as-is
    const exportData = productList.map((row) => [
      row.product_id || "",
      row.sku || "",
      row.source_store_id || "",
      row.destination_store_id || "",
      // The upload note says DD-MM-YYYY, so the export writes it that way too.
      row.manufacture_date
        ? moment(row.manufacture_date, ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601], true).isValid()
          ? moment(row.manufacture_date, ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601], true).format("DD-MM-YYYY")
          : row.manufacture_date
        : "",
      // ?? so a genuine 0 is kept, not blanked.
      row.life_days ?? "",
      row.buying_price ?? "",
      row.selling_price ?? "",
      row.adjustmentType || "",
      row.stockAdjustment ?? "",
      row.reason || "",
      row.note || "",
      // row.out_of_stock === 1 ? "Yes" : "No",
    ]);

    const wsData = [...notes, headers, ...exportData];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Step 5: Auto-adjust column widths (optional but looks cleaner)
    const colWidths = headers.map((header) => ({
      wch: Math.max(header.length + 2, 20),
    }));
    ws["!cols"] = colWidths;

    // Step 6: Create workbook and export
    const wb = XLSX.utils.book_new();
    fitColumns(ws);
    XLSX.utils.book_append_sheet(wb, ws, "Stock_Data");
    writeXlsxFrozen(XLSX, wb, "Stock_adjustment_Data.xlsx");
  };

  const ExportTemplate = () => {
    const notes = [
      ["Note: Use 'Destination Store' only for stock transfer."],
      [
        "For 'Add': 'Manufacture Date', 'Expiry Days', 'Buying Price', and 'Selling Price' are required.",
      ],
      ["For 'Deduct': 'Manufacture Date' and 'Expiry Days' are required."],
      [
        "For 'Transfer': 'Manufacture Date', 'Expiry Days', 'Buying Price', and 'Selling Price' are not required.",
      ],
      [
        "Additional Info: Ensure date format is DD-MM-YYYY.",
      ],
      [], // Empty row for spacing before headers
    ];

    const columnHeaders = [
      [
        "Product",
        "SKU",
        "Source Store",
        "Destination Store",
        "Manufacture Date(DD-MM-YYYY)",
        "Expiry Days",
        "Buying Price",
        "Selling Price",
        "Adjustment Type (add/deduct/transfer)", // <-- instruction added
        "Quantity",
        "Reason",
        "Reference note(Optional)",
        // "Out of Stock (Yes/No)", // <-- instruction added
      ],
    ];

    const wsData = [...notes, ...columnHeaders];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    fitColumns(ws);
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    writeXlsxFrozen(XLSX, wb, "Stock_adjustment_Template.xlsx");
  };

  const handleSubmit = async () => {
    setLoading(true);
    setShowStatus(true);

    const updatedData = productList.map((obj) => ({
      ...obj,
      created_by: user.emp_id,
    }));

    const payload = { data: updatedData };

    const result = await apiData("post", stockBulkUploadAPI, payload);

    if (result.data) {
      const { success: totalSuccess, failed, responses } = result.data;

      // Update counts
      setSuccessCount(totalSuccess);
      setPendingCount(failed);

      // Keep only pending rows in table
      const failedItems = responses
        .filter((r) => !r.success)
        .map((r) => r.item);
      setProductList(failedItems);

      // Show alert:
      // If any failed items, show first error message
      const firstError = responses.find((r) => !r.success);
      const message = firstError
        ? `Error: ${firstError.item.product_name || firstError.item.sku} - ${
            firstError.message
          }`
        : "All Stocks uploaded successfully";

      alertRef.current.triggerAlert(
        JSON.stringify({
          message,
          messageType: failed > 0 ? "warning" : "success",
        })
      );
    } else {
      // fallback if something went wrong
      alertRef.current.triggerAlert(
        JSON.stringify({
          message: result.msg || "An unexpected error occurred",
          messageType: "error",
        })
      );
    }

    setLoading(false);
  };

  const handleFileUpload = ($event) => {
    setProductList([]);
    setPendingCount(0);
    setSuccessCount(0);
    setFileLoading(true);

    const files = $event.target.files;

    const parseDate = (value) => {
      if (!value) return "";

      // If it's already a Date object
      if (value instanceof Date && !isNaN(value)) {
        const day = String(value.getDate()).padStart(2, "0");
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const year = value.getFullYear();
        return `${year}-${month}-${day}`;
      }

      // If Excel gave a numeric date (Excel serial number)
      if (!isNaN(value)) {
        const date = XLSX.SSF.parse_date_code(value);
        if (date) {
          const d = new Date(date.y, date.m - 1, date.d);
          const day = String(d.getDate()).padStart(2, "0");
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const year = d.getFullYear();
          return `${year}-${month}-${day}`;
        }
      }

      // If it's a string, handle DD-MM-YYYY or DD/MM/YYYY or YYYY-MM-DD
      const parts = value
        .toString()
        .trim()
        .split(/[-/]/)
        .map((p) => p.trim());
      if (parts.length !== 3) return "";

      let day, month, year;
      if (parts[0].length === 4) {
        // Already YYYY-MM-DD
        [year, month, day] = parts;
      } else if (parseInt(parts[0], 10) > 12) {
        // DD-MM-YYYY
        [day, month, year] = parts;
      } else {
        // MM-DD-YYYY
        [month, day, year] = parts;
      }

      if (year.length === 2) year = "20" + year;

      const d = new Date(`${year}-${month}-${day}`);
      if (isNaN(d)) return "";

      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;
    };

    const file = files[0];
    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "application/vnd.ms-excel", // .xls
      "text/csv", // .csv
    ];

    if (!allowedTypes.includes(file.type)) {
      setFileLoading(false);
      alertRef.current.triggerAlert(
        JSON.stringify({
          message: "Invalid file type. Please upload an Excel or CSV file.",
          messageType: "error",
        })
      );
      $event.target.value = "";

      return;
    }

    if (files) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const wb = read(event.target.result);
        const sheets = wb.SheetNames;

        if (sheets.length) {
          const rows = utils.sheet_to_json(wb.Sheets[sheets[0]], {
            header: 1,
            raw: false,
            dateNF: "yyyy-mm-dd",
            cellDates: true,
          });

          const dataRows = rows.slice(6);

          const headers = dataRows[0];
          const actualData = dataRows.slice(1);

          const headerMap = {
            Product: "product_id",
            SKU: "sku",
            "Source Store": "source_store_id",
            "Destination Store": "destination_store_id",
            "Manufacture Date(DD-MM-YYYY)": "manufacture_date",
            "Expiry Days": "life_days",
            "Buying Price": "buying_price",
            "Selling Price": "selling_price",
            "Adjustment Type (add/deduct/transfer)": "adjustmentType",
            Quantity: "stockAdjustment",
            Reason: "reason",
            "Reference note(Optional)": "note",
            // "Out of Stock (Yes/No)": "out_of_stock",
          };

          const updatedData = actualData
            .map((row) => {
              const isEmptyRow = row.every(
                (cell) =>
                  cell === undefined ||
                  cell === null ||
                  cell.toString().trim() === ""
              );
              if (isEmptyRow) return null;
              const obj = {};
              headers.forEach((header, i) => {
                const key = headerMap[header] || header;
                obj[key] = row[i];
              });

              if (
                !obj.out_of_stock ||
                obj.out_of_stock.toString().trim() === ""
              ) {
                obj.out_of_stock = ""; // leave empty string
              }

              obj.manufacture_date = parseDate(obj.manufacture_date);
              return obj;
            })
            .filter(Boolean);

          const validatedData = await Promise.all(
            updatedData.map(async (item) => {
              if (item.out_of_stock === undefined || item.out_of_stock === "") {
                item.out_of_stock = 0;
              }
              const adjustmentType = item.adjustmentType
                ? item.adjustmentType
                    .toString()
                    .trim()
                    .toLowerCase()
                    .replace(/\s+/g, "")
                : "";
              item.adjustmentType = adjustmentType;
              const schema = getValidationSchema(adjustmentType);
              try {
                await schema.validate(item, { abortEarly: false });
                return { ...item, errorMessages: null };
              } catch (err) {
                return {
                  ...item,
                  errorMessages: err.errors.join(", "),
                };
              }
            })
          );

          const finalData = validatedData.map((item) => ({
            ...item,
            out_of_stock:
              item.out_of_stock?.toString().toLowerCase() === "yes" ? 1 : 0,
          }));

          setProductList(finalData);
          const hasErrors = finalData.some((row) => row.errorMessages);
          setShowValidationColumn(hasErrors);
        }
        setFileLoading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      setFileLoading(false);
    }
  };

  //   const lgUp = useMediaQuery((theme) => theme.breakpoints.up('lg'));

  const columns = useMemo(() => {
    const baseColumns = [
      { accessorKey: "product_id", header: "Product Name", size: 200 },
      { accessorKey: "sku", header: "SKU", size: 200 },
      { accessorKey: "source_store_id", header: "Source Store", size: 200 },
      {
        accessorKey: "destination_store_id",
        header: "Destination Store",
        size: 200,
      },
      {
        accessorKey: "manufacture_date",
        header: "Manufacture Date",
        size: 200,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return "";
          const date = new Date(value);
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          return `${day}-${month}-${year}`;
        },
      },
      { accessorKey: "life_days", header: "Expiry Days", size: 200 },
      { accessorKey: "adjustmentType", header: "Adjustment Type", size: 200 },
      {
        accessorKey: "buying_price",
        header: "Buying Price",
        size: 200,
        Cell: ({ cell }) => {
          const v = cell.getValue();
          return v === "" || v === null || v === undefined ? "" : formatAED(v);
        },
      },
      {
        accessorKey: "selling_price",
        header: "Selling Price",
        size: 200,
        Cell: ({ cell }) => {
          const v = cell.getValue();
          return v === "" || v === null || v === undefined ? "" : formatAED(v);
        },
      },
      {
        accessorKey: "stockAdjustment",
        header: "Quantity",
        size: 200,
        Cell: ({ cell }) => {
          const v = cell.getValue();
          return v === "" || v === null || v === undefined ? "" : formatNumber(v);
        },
      },
      { accessorKey: "reason", header: "Reason", size: 200 },
      { accessorKey: "note", header: "Reference note(Optional)", size: 200 },
      // {
      //   accessorKey: "out_of_stock",
      //   header: "Out of Stock",
      //   size: 140,
      //   Cell: ({ cell }) => (cell.getValue() === 1 ? "Yes" : "No"),
      // },
    ];

    if (showValidationColumn) {
      baseColumns.push({
        accessorKey: "errorMessages",
        header: "Validation Status",
        size: 200,
        // ✅ Sticky body cells
        muiTableBodyCellProps: {
          sx: {
            position: "sticky",
            right: 0,
            backgroundColor: "#fff",
            zIndex: 1,
          },
        },
        // ✅ Sticky header
        muiTableHeadCellProps: {
          sx: {
            position: "sticky",
            right: 0,
            backgroundColor: "#f5f5f5", // optional: distinguish header
            zIndex: 2,
          },
        },
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return (
            <span style={{ color: value ? "red" : "green" }}>
              {value || "No errors"}
            </span>
          );
        },
      });
    }
    return baseColumns;
  }, [showValidationColumn]);

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <AlertMessage ref={alertRef} />

        <Grid container spacing={2} alignItems="center">
          {/* LEFT SIDE — Title + Breadcrumb */}
          <Grid item xs={12} md={6} lg={6}>
            <Typography className="page_title">Stock - Bulk Upload</Typography>

            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">
                Stock Adjustments / Transfer
              </Typography>
              <Typography color="text.primary">Bulk Upload</Typography>
            </Breadcrumbs>
          </Grid>

          {/* RIGHT SIDE — Buttons in one line like reference */}
          <Grid item xs={12} md={6} lg={6}>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                justifyContent: { xs: "flex-start", md: "flex-end" },
                flexWrap: { xs: "wrap", md: "wrap" },
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                href="/reports/adjustment-report"
                className="button_first"
                sx={{
                  fontSize: { xs: "0.65rem", md: "0.7rem", lg: "0.75rem" },
                  padding: { xs: "6px 10px", md: "7px 12px", lg: "9px 14px" },
                  lineHeight: 1.2,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                }}
              >
                Stock Adjustment Report
              </Button>

              <Button
                variant="contained"
                color="secondary"
                href="/reports/transaction-report"
                className="button_first"
                sx={{
                  fontSize: { xs: "0.65rem", md: "0.7rem", lg: "0.75rem" },
                  padding: { xs: "6px 10px", md: "7px 12px", lg: "9px 14px" },
                  lineHeight: 1.2,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                }}
              >
                Stock Transaction Report
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(-1)}
                startIcon={<ArrowBackIosIcon />}
                sx={{
                  fontSize: { xs: "0.65rem", md: "0.7rem", lg: "0.75rem" },
                  padding: { xs: "5px 10px", md: "6px 12px", lg: "8px 14px" },
                  lineHeight: 1.2,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                }}
              >
                Back
              </Button>
            </Box>
          </Grid>
        </Grid>

        <br></br>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1000 }}
          open={fileLoading || loading}
        >
          <Box textAlign="center">
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              {fileLoading
                ? "Parsing file... please wait"
                : `Uploading ${productList.length} records... please wait, this may take a few minutes.`}
            </Typography>
          </Box>
        </Backdrop>

        <>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              {/* <TextField type="file" onChange={handleFileUpload} /> */}
              <TextField
                type="file"
                name="file"
                className="custom-file-input"
                id="inputGroupFile"
                required
                inputRef={fileInputRef}
                onChange={handleFileUpload}
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              />

              <Button
                onClick={ExportTemplate}
                style={{ background: "#1a73e829" }}
                // sx={{ ml: 8 }}
                startIcon={<FileDownload />}
              >
                Export Template
              </Button>
            </Grid>

            {/* <TextField type="file" onChange={handleFileUpload} /> */}
            {productList.length > 0 && (
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <Box display={"flex"} gap={1}>
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={productList.some((item) => item.errorMessages)}
                    sx={{ mt: 3, mb: 2 }}
                    onClick={() => {
                      handleSubmit();
                    }}
                  >
                    Submit
                  </Button>
                </Box>
              </Grid>
            )}
            <Grid item xs={12} display="flex" width={0}>
              <MaterialReactTable
                columns={columns}
                enableFullScreenToggle={false}
                data={productList}
                getRowId={(row) => row.name}
                enableGlobalFilter={false}
                enableRowNumbers
                rowNumberMode="original"
                enableColumnActions={false}
                enableColumnFilters={false}
                enablePagination={false}
                enableSorting={false}
                enableStickyHeader={true}
                enableRowVirtualization={false}
                muiTableContainerProps={{ sx: { maxHeight: "600px" } }}
                rowVirtualizerOptions={{ overscan: 5 }}
                //   initialState={{
                //     columnPinning: lgUp ? { right: ['Status'] } : {},
                //   }}
                renderTopToolbarCustomActions={({ table }) => (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        gap: "1rem",
                        p: "0.5rem",
                        justifyContent: "space-between",
                        flexGrow: 1,

                        flexWrap: "wrap",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {/* RIGHT SIDE BUTTON */}
                        <Tooltip title="Export data to excel sheet" arrow>
                          <Button
                            onClick={handleExportData}
                            style={{ background: "#1a73e829" }}
                            startIcon={<FileDownload />}
                            // startIcon={<IconTableExport />}
                          >
                            Export To Excel
                          </Button>
                        </Tooltip>
                      </Box>

                      {productList.length > 0 && showStatus && (
                        <Box display={"flex"} alignItems="center" gap={1}>
                          <Chip
                            label={`Added: ${successCount}`}
                            color="success"
                            variant="outlined"
                          />
                          <Chip
                            label={`Pending: ${pendingCount}`}
                            color="warning"
                            variant="outlined"
                          />
                        </Box>
                      )}
                    </Box>
                  </>
                )}
              />
            </Grid>
          </Grid>
        </>
      </Grid>
    </Layout>
  );
};

export default StockBulkUpload;
